import { Alert, Button, Group, Menu, Stack, TagsInput, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconArrowForwardUp,
  IconChevronDown,
  IconInfoCircle,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react';
import { useSetAtom } from 'jotai';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { isEmpty } from 'remeda';

import { type Reminder, useForwardReminder } from '../api';

import { resendReminderModalAtom } from './ResendReminderModal';

type ForwardReminderFormProps = {
  reminderId: number;
};

const ForwardReminderForm = ({ reminderId }: ForwardReminderFormProps) => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<Array<string>>([]);
  const { forwardReminder, isForwardReminderLoading } = useForwardReminder();

  return (
    <Stack>
      <Alert variant="light" icon={<IconInfoCircle stroke={1.5} />}>
        {t(i18nKeys.REMINDER.SEND_AGAIN.FORWARD_INFO)}
      </Alert>
      <TagsInput
        label={t(i18nKeys.REMINDER.SEND_AGAIN.FORWARD_EMAILS_LABEL)}
        placeholder={t(i18nKeys.FAILED_REMINDERS.MODALS.RESEND.EXTERNAL_EMAIL_PLACEHOLDER)}
        description={t(i18nKeys.FAILED_REMINDERS.MODALS.RESEND.SUBMIT_HINT)}
        inputWrapperOrder={['label', 'input', 'description']}
        value={emails}
        onChange={setEmails}
      />
      <Group justify="flex-end">
        <Button color="orange" variant="light" onClick={() => modals.closeAll()}>
          {t(i18nKeys.FAILED_REMINDERS.MODALS.CANCEL)}
        </Button>
        <Button
          loading={isForwardReminderLoading}
          disabled={isEmpty(emails)}
          leftSection={<IconArrowForwardUp stroke={1.5} size={18} />}
          onClick={() =>
            forwardReminder({ id: reminderId, emails }, { onSuccess: () => modals.closeAll() })
          }
        >
          {t(i18nKeys.REMINDER.SEND_AGAIN.FORWARD_SUBMIT)}
        </Button>
      </Group>
    </Stack>
  );
};

type SentReminderActionsProps = {
  reminder: Reminder;
};

export const SentReminderActions = ({ reminder }: SentReminderActionsProps) => {
  const { t } = useTranslation();
  const dispatchResendModal = useSetAtom(resendReminderModalAtom);

  if (reminder.status === 'failed') return null;

  const openForwardModal = () =>
    modals.open({
      title: t(i18nKeys.REMINDER.SEND_AGAIN.FORWARD_MODAL_TITLE),
      children: <ForwardReminderForm reminderId={reminder.id} />,
    });

  return (
    <Menu position="bottom-end" width={320}>
      <Menu.Target>
        <Button
          variant="light"
          leftSection={<IconSend stroke={1.5} size={16} />}
          rightSection={<IconChevronDown stroke={1.5} size={16} />}
        >
          {t(i18nKeys.REMINDER.SEND_AGAIN.BUTTON)}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconArrowForwardUp stroke={1.5} size={18} />}
          onClick={openForwardModal}
        >
          <Text size="sm" fw={500}>
            {t(i18nKeys.REMINDER.SEND_AGAIN.FORWARD)}
          </Text>
          <Text size="xs" c="dimmed">
            {t(i18nKeys.REMINDER.SEND_AGAIN.FORWARD_DESCRIPTION)}
          </Text>
        </Menu.Item>
        <Menu.Item
          leftSection={<IconRefresh stroke={1.5} size={18} />}
          disabled={reminder.status === 'resent'}
          onClick={() => dispatchResendModal({ type: 'open', reminder, canIgnore: false })}
        >
          <Text size="sm" fw={500}>
            {t(i18nKeys.REMINDER.SEND_AGAIN.RESEND)}
          </Text>
          <Text size="xs" c="dimmed">
            {t(i18nKeys.REMINDER.SEND_AGAIN.RESEND_DESCRIPTION)}
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
